export interface PartnerWhatsAppContext {
  relationshipStatus: string
  childrenCount: number
}

export const PARTNER_WHATSAPP_QUESTIONS = [
  'What values matter most to you in your life and in our relationship?',
  'What helps you feel genuinely seen, loved and supported by me?',
  'What helps you decompress, feel safe to exhale or carry less load?',
  'Which gestures, experiences, gifts or important occasions make you feel especially cared for?',
  'Are there boundaries or preferences you want me to understand and respect more clearly?',
] as const

export function partnerGrowthRoles({ relationshipStatus, childrenCount }: PartnerWhatsAppContext): string {
  const isFather = childrenCount > 0
  if (relationshipStatus === 'married') return isFather ? 'a better man, husband and father' : 'a better man and husband'
  return isFather ? 'a better man, partner and father' : 'a better man and partner'
}

export function buildPartnerWhatsAppMessage(context: PartnerWhatsAppContext): string {
  const questions = PARTNER_WHATSAPP_QUESTIONS.map((question, index) => `${index + 1}. ${question}`).join('\n\n')

  return [
    'Hey love ❤️',
    'I want to share something important with you.',
    `I’m working with a self-development and relationship coach because I want to become ${partnerGrowthRoles(context)}. I do not want this to be about saying the right things. I want to understand myself better, take more responsibility, communicate more honestly and love the people closest to me more intentionally.`,
    'Part of this process is learning to understand you through your own words instead of assuming I already know what you feel or need. Your honest answers would help me listen better and understand what genuinely helps you feel seen, loved, supported and respected.',
    'There are no perfect answers, and I do not want you to write what you think I want to hear. The more open and truthful you can be, the more useful this will be. You can skip anything you would rather keep private, answer in your own time, or ask that we talk about it together instead.',
    'When you have the space, would you answer these in your own words?',
    questions,
    'You do not need to answer everything at once. Thank you for helping me know you better. I love you, and I want this work to become something you can feel in how I show up—not just something I say.',
  ].join('\n\n')
}

export function normaliseWhatsAppNumber(input: string): string | null {
  const digits = input.trim().replace(/\D/g, '')
  const internationalDigits = digits.startsWith('00') ? digits.slice(2) : digits
  return /^[1-9]\d{7,14}$/.test(internationalDigits) ? internationalDigits : null
}

export function buildPartnerWhatsAppUrl(input: string, context: PartnerWhatsAppContext): string | null {
  const number = normaliseWhatsAppNumber(input)
  if (!number) return null
  return `https://wa.me/${number}?text=${encodeURIComponent(buildPartnerWhatsAppMessage(context))}`
}
